import { useEffect, useMemo, useState } from "react";
import useProjectDomains from "../../../../api/utilities/useProjectDomains.js";
import { ProjectApi } from "../../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../../api/utilities/useAuthorityConfiguration.js";
import Creatable from "react-select/creatable";
import Button from "../../../../components/Button.jsx";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import Spinner from "../../../../components/Spinner.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import {IntegrationAddButton} from "../IntegrationDetailUi.jsx";
import StoredSecretInput from "./StoredSecretInput.jsx";

export default function HostNameApiIdApiKeyList({
                                                     project,
                                                     projectIntegration,
                                                     value,
                                                     onChange,
                                                     apiIdPlaceholder = 'ShopID',
                                                     apiKeyPlaceholder = 'API key',
                                                     apiKeyMultiline = false,
                                                 }) {
    const authorityConfiguration = useAuthorityConfiguration();
    const projectDomains = useProjectDomains(project);
    const [projectDomainNameWithApiIdApiKeyList, setProjectDomainNameWithApiIdApiKeyList] = useState(null);

    useEffect(() => {
        if (projectIntegration === null) {
            setProjectDomainNameWithApiIdApiKeyList([]);
            return;
        }

        new ProjectApi(authorityConfiguration)
            .hostNameApiIdApiKey({
                integrationId: projectIntegration.id
            })
            .then(setProjectDomainNameWithApiIdApiKeyList)
            .catch(() => setProjectDomainNameWithApiIdApiKeyList([]));
    }, [project.id, projectIntegration?.id]);

    const hostNameOptions = useMemo(() => {
        if (projectDomains === null) return [];
        return projectDomains.map((projectDomain) => ({
            value: projectDomain.projectDomain.hostName,
            label: projectDomain.projectDomain.hostName
        }));
    }, [projectDomains]);

    useEffect(() => {
        if (projectDomainNameWithApiIdApiKeyList !== null && value.length === 0) {
            const initialValue = projectDomainNameWithApiIdApiKeyList.length > 0
                ? projectDomainNameWithApiIdApiKeyList.map((row) => ({...row, apiKey: ''}))
                : [{ hostName: '', apiId: '', apiKey: '' }];
            onChange(initialValue);
        }
    }, [projectDomainNameWithApiIdApiKeyList]);

    const handleDelete = (index) => {
        const newValue = value.filter((_, checkedIdx) => index !== checkedIdx);
        if (newValue.length === 0) {
            onChange([{ hostName: '', apiId: '', apiKey: '' }]);
        } else {
            onChange(newValue);
        }
    };

    const handleAdd = () => {
        onChange([...value, { hostName: '', apiId: '', apiKey: '' }]);
    };

    return (
        <LoadingComponent loading={projectDomainNameWithApiIdApiKeyList === null}>
            {projectDomains === null && <Spinner />}
            {projectDomains && (
                <div className={'flex flex-col gap-3'}>
                    {(value ?? []).map((row, index) => (
                        <div className={'rounded-xl border border-[#ddd] bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40'} key={`api-settings-row-${index}`}>
                            <div className={'mb-4 flex items-center justify-between gap-3'}>
                                <div className={'text-sm font-semibold text-slate-700 dark:text-slate-200'}>Nastavení domény #{index + 1}</div>
                                <Button className={'!h-8 !w-8 !border-red-500 !bg-red-500 !p-0 !text-sm !text-white hover:!border-red-600 hover:!bg-red-600'} onClick={() => handleDelete(index)} title={'Odebrat doménu'}>
                                    <FontAwesomeIcon icon={faTrashCan} />
                                </Button>
                            </div>
                            <div className={'grid gap-4 md:grid-cols-2'}>
                                <label className={'flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200'}>Doména e-shopu
                                <Creatable
                                    classNamePrefix={'select'}
                                    value={{ label: row.hostName, value: row.hostName }}
                                    onChange={(e) => {
                                        const newValue = [...value];
                                        newValue[index] = { ...row, hostName: e.value };
                                        onChange(newValue);
                                    }}
                                    options={hostNameOptions}
                                    placeholder={'Vyberte nebo zadejte doménu'}
                                />
                                </label>
                                <label className={'flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200'}>{apiIdPlaceholder}
                                <input
                                    placeholder={apiIdPlaceholder}
                                    value={row.apiId}
                                    className={'form-input'}
                                    type={'text'}
                                    onChange={(e) => {
                                        const newValue = [...value];
                                        newValue[index] = { ...row, apiId: e.target.value };
                                        onChange(newValue);
                                    }}
                                />
                                </label>
                            </div>
                            <label className={'mt-4 flex flex-col gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200'}>{apiKeyPlaceholder}
                                <StoredSecretInput
                                    configured={row.apiKeyConfigured ?? false}
                                    placeholder={apiKeyPlaceholder}
                                    value={row.apiKey ?? ''}
                                    onChange={(apiKey) => {
                                        const newValue = [...value];
                                        newValue[index] = {...row, apiKey};
                                        onChange(newValue);
                                    }}
                                />
                            </label>
                        </div>
                    ))}
                    <div className={'flex justify-center pt-3'}>
                        <IntegrationAddButton onClick={handleAdd}>
                            Přidat doménu
                        </IntegrationAddButton>
                    </div>
                </div>
            )}
        </LoadingComponent>
    );
}
